import { Layers, Split, Scale, CircleHelp } from "lucide-react";
import { Reveal, SectionHeading } from "../Reveal";

const PAINS = [
  {
    icon: Layers,
    title: "Too many options",
    copy: "Every program, country, and career path claims to be the right one. Comparing them all is a full-time job.",
  },
  {
    icon: Split,
    title: "Conflicting advice",
    copy: "Forums, relatives, and agents all say something different — and each one has their own agenda.",
  },
  {
    icon: Scale,
    title: "High-stakes decisions",
    copy: "One wrong choice can cost years of study, thousands in tuition, and momentum you can't get back.",
  },
  {
    icon: CircleHelp,
    title: "Fear of the wrong choice",
    copy: "So you delay. Deadlines pass. And the gap between where you are and where you want to be keeps growing.",
  },
];

export function Problem() {
  return (
    <section id="problem" data-testid="problem-section" className="bg-white py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <SectionHeading
                chapter={["01", "The Challenge"]}
                title={
                  <>
                    Confused about your education or{" "}
                    <em className="font-editorial font-medium italic text-gold-dark">career path?</em>
                  </>
                }
                subtitle="You're not behind — you're just unsupported. Most people aren't lacking ambition; they're lacking a clear, trusted plan. Here's what usually stands in the way."
              />
              <Reveal delay={0.2}>
                <div className="mt-8 rounded-2xl border border-gold/30 bg-gold/5 p-5">
                  <p className="font-editorial text-lg italic leading-relaxed text-forest-deep">
                    “The cost of the wrong path is always higher than the cost of the right guidance.”
                  </p>
                </div>
              </Reveal>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="space-y-5" data-testid="pain-points-list">
              {PAINS.map((p, i) => (
                <Reveal key={p.title} delay={i * 0.09}>
                  <div
                    data-testid={`pain-point-${i}`}
                    className="group flex gap-6 rounded-3xl border border-border bg-bone p-6 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:border-forest/25 hover:shadow-[0_18px_45px_rgba(10,41,30,0.08)] sm:p-7"
                  >
                    <span className="font-mono text-sm font-semibold text-gold-dark">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <p.icon className="h-5 w-5 text-forest" />
                        <h3 className="font-display text-lg font-bold text-forest-deep sm:text-xl">{p.title}</h3>
                      </div>
                      <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground sm:text-base">{p.copy}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
